import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

/** Hover intent helper to avoid flicker */
function useHoverIntent(delayOpen = 90, delayClose = 120) {
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const scheduleOpen = (fn) => {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
    if (openTimer.current) window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(fn, delayOpen);
  };
  const scheduleClose = (fn) => {
    if (openTimer.current) { window.clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(fn, delayClose);
  };
  useEffect(() => () => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);
  return { scheduleOpen, scheduleClose };
}

const MENUS = [
  {
    label: "Pitching Analysis",
    items: [
      { label: "Featured Pitchers", to: "/pitching/overview" },
      { label: "Pitcher Scouting Reports", to: "/pitching/scouting-reports" },
      { label: "Pitch Type Analysis", to: "/pitching/types" },
      { label: "Featured Pitcher Dev Notes", to: "/pitcher-development-plans" },
    ],
  },
  {
    label: "Hitting Analysis",
    items: [
      { label: "Featured Hitters", to: "/hitting/overview" },
      { label: "Hitter Scouting Reports", to: "/hitting/reports" },
      { label: "Hitter Spray Profiles", to: "/hitting/spray-charts" },
      { label: "Featured Hitter Dev Notes", to: "/hitter-development-plans" },
    ],
  },
];

export default function NavBar() {
  const [openIndex, setOpenIndex] = useState(null);
  const rootRef = useRef(null);
  const location = useLocation();
  const { scheduleOpen, scheduleClose } = useHoverIntent();

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      "[NavBar] mounted from", 
      __filename || "unknown-file",
      " pathname:", window.location.pathname
    );
  }, []);

  // Close on route change
  useEffect(() => setOpenIndex(null), [location.pathname]);

  // Close on click outside
  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpenIndex(null);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Close on ESC
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") setOpenIndex(null); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Dump computed styles for debugging
  useEffect(() => {
    const root = document.querySelector("nav.nav-skin");
    if (!root) return;
    const triggers = root.querySelectorAll("a, button");
    triggers.forEach((el) => {
      const cs = window.getComputedStyle(el);
      console.log("[NavBar] trigger:", el.innerText, {
        color: cs.color,
        opacity: cs.opacity,
      });
    });
  }, []);

  const menuRefs = useMemo(() => MENUS.map(() => React.createRef()), []);

  const handleTriggerMouseEnter = (i) => {
    if (!MENUS[i].items) return;
    scheduleOpen(() => setOpenIndex(i));
  };
  const handleTriggerMouseLeave = (i) => {
    if (!MENUS[i].items) return;
    scheduleClose(() => setOpenIndex((prev) => (prev === i ? null : prev)));
  };
  const handleMenuMouseEnter = (i) => scheduleOpen(() => setOpenIndex(i));
  const handleMenuMouseLeave = (i) => scheduleClose(() => setOpenIndex((prev) => (prev === i ? null : prev)));

  const onTriggerKeyDown = (e, i) => {
    if (!MENUS[i].items) return;
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") { e.preventDefault(); setOpenIndex(i); }
    if (e.key === "ArrowUp") { e.preventDefault(); setOpenIndex(i); }
  };

  return (
    <nav ref={rootRef} className="nav-skin px-8 py-5 sticky top-0 z-50" style={{
      background: 'linear-gradient(135deg, #0B2E5B 0%, #1A3B6B 100%)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <div className="max-w-7xl mx-auto flex justify-center items-center">
        <div className="flex items-center font-medium text-white" style={{gap: '2.5rem'}}>
          {/* Home Button */}
          <Link 
            to="/" 
            className="text-sm font-medium tracking-wide uppercase text-white/90 transition-all duration-300 ease-out hover:text-yellow-400 hover:scale-105 relative group"
            onClick={() => setOpenIndex(null)}
            style={{
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}
          >
            <span className="relative z-10">Home</span>
            <span className="absolute bottom-[-4px] left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
          </Link>
          
          {/* Navigation Menu Items */}
          {MENUS.map((m, i) => {
          const hasMenu = !!m.items?.length;
          const isOpen = openIndex === i && hasMenu;
          return (
            <div
              key={m.label}
              className="relative"
              onMouseEnter={() => handleTriggerMouseEnter(i)}
              onMouseLeave={() => handleTriggerMouseLeave(i)}
              style={{position: 'relative'}}
            >
              {m.to ? (
                <Link className="text-sm font-medium tracking-wide uppercase text-white/90 transition-all duration-300 ease-out hover:text-yellow-400 hover:scale-105 relative group" to={m.to} style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                }}>
                  <span className="relative z-10">{m.label}</span>
                  <span className="absolute bottom-[-4px] left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              ) : (
                <button
                  className="flex items-center gap-2 text-sm font-medium tracking-wide uppercase text-white/90 transition-all duration-300 ease-out hover:text-yellow-400 hover:scale-105 outline-none relative group"
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                  aria-label={`${m.label} menu`}
                  onKeyDown={(e) => onTriggerKeyDown(e, i)}
                  onClick={() => setOpenIndex((prev) => (prev === i ? null : i))}
                  style={{
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}
                >
                  <span className="relative z-10">{m.label}</span>
                  <svg 
                    className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'rotate-180 text-yellow-400' : 'text-white/70'}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="absolute bottom-[-4px] left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                </button>
              )}

              {hasMenu && (
                <div
                  ref={menuRefs[i]}
                  role="menu"
                  aria-label={m.label}
                  className={`dropdown-skin absolute left-0 top-[calc(100%+12px)] w-80 p-4 transform-gpu will-change-[opacity,transform] transition-all duration-200 ease-out origin-top-left ${
                    isOpen
                      ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
                      : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
                  }`}
                  style={{
                    background: 'linear-gradient(135deg, #0C2040 0%, #1A3B6B 100%)',
                    border: '1px solid rgba(241, 196, 59, 0.2)',
                    borderRadius: '0.75rem',
                    boxShadow: '0 20px 40px -8px rgba(0, 0, 0, 0.4), 0 8px 16px -4px rgba(11, 46, 91, 0.3), 0 0 0 1px rgba(241, 196, 59, 0.1)',
                    backdropFilter: 'blur(8px)'
                  }}
                  onMouseEnter={() => handleMenuMouseEnter(i)}
                  onMouseLeave={() => handleMenuMouseLeave(i)}
                >
                  {m.items.map((it) => (
                    <Link
                      key={it.label}
                      role="menuitem"
                      to={it.to}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-white/80 hover:text-white hover:bg-yellow-400/10 focus:bg-yellow-400/10 focus:outline-none transition-all duration-200 ease-out group"
                      onClick={() => setOpenIndex(null)}
                    >
                      <span className="w-2 h-2 rounded-full bg-yellow-400/60 group-hover:bg-yellow-400 transition-all duration-200"></span>
                      <span className="flex-1">{it.label}</span>
                      <svg className="w-4 h-4 text-white/40 group-hover:text-yellow-400 transition-all duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </nav>
  );
}
